import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { SetFiltering } from 'state/actions';
import { UserAccess, UserHasAccess } from "components/user-access";
import { PageRestricted } from "pages/errors/page-restricted";
import { FormDatePeriodSelector, FormLocationSelector, FormTextInput } from "components/form-fields"
import { Tooltip } from "components/tooltip";
import { Button, LinkButton } from "components/buttons"
import { Filter, FilterButton } from "components/filtering";
import { NotificationToaster, NotificationInline } from "components/notifications";
import { Intent, Position } from "@blueprintjs/core";
import { ListingPagination, ListingTable } from "components/listing";
import { AssetService, LinkService } from 'services';
import { KeyBlock } from "components/key/KeyBlock";
import { Numeric } from "components/formatting/Numeric";
import { DateToLocal } from "components/dates/DateToLocal";
import { PreferencesModal } from "components/preferences-modal/PreferencesModal";
import { KeyModal } from "./key-modal";
import { DayDetailModal } from "./day-detail-modal";
import { ReportList } from "components/reporting/ReportList";
import { AssetStatus } from 'components/status/AssetStatus';
import { useDebounce } from "hooks/useDebounce";
import { InternalLink } from 'components/navigation';
import { AssetTypeFilterRenderer } from "./asset-type-filter-renderer/AssetTypeFilterRenderer";
import classNames from "classnames";
import moment from "moment";
import "./Vehicle.css"

export function Vehicle() {
    const requiredActions = ["Fleet:View"];
    const canAccessPage = UserHasAccess(requiredActions);
    const hasFleet = UserHasAccess("FMS:View");
    const firstColumnLabel = "ID/Registration";
    const calculatedColumnNames = ["KM without card"];
    let tableHeadersPrefix = [firstColumnLabel, ""];
    const initialDate = moment().startOf('isoWeek');
    
    const assetTypesMappings = [
        { "id": 1, "img": "hgv-scaled.svg", "name": "Heavy Goods Vehicle" },
        { "id": 2, "img": "lcv-scaled.svg", "name": "Light Commercial Vehicle" },
        { "id": 3, "img": "psv-scaled.svg", "name": "Public Service Vehicle" },
        { "id": 4, "img": "trailer-scaled.svg", "name": "Trailer" },
        { "id": 5, "img": "dst-scaled.svg", "name": "Dangerous Substances Tanker" },
        { "id": 6, "img": "crane-scaled.svg", "name": "Crane" },
        { "id": 7, "img": "tail lift-scaled.svg", "name": "Tail Lift" },
        { "id": 8, "img": "lift truck-scaled.svg", "name": "Lift Truck" },
        { "id": 9, "img": "winch-scaled.svg", "name": "Winch" },
        { "id": 10, "img": "hgv-scaled.svg", "name": "Vehicle" },
        { "id": 12, "img": "van-scaled.svg", "name": "Van" },
        { "id": 13, "img": "car-scaled.svg", "name": "Car" },
        { "id": 14, "img": "lifting equipment-scaled.svg", "name": "Lifting Equipment" },
        { "id": 15, "img": "trailer-scaled.svg", "name": "Light Trailer" }
    ];

    const periodSelectorDateFormat = "DD MMM YYYY";
    const defaultItemsPerPage = 25;
    const daysInWeek = 7;
    const saturdayInt = 6;
    const sundayInt = 7;
    const cellWidthIdentifier = 175;
    const cellWidthDefault = 100;
    const cellWidthDay = 40;
    const debounceTimeout = 750;
    const filterName = "vehicleAssets";
    const filterSearchSelector = "vehicleSearch";
    const vehicleDateReduxName = "vehicleDate";
    const weekendDays = [saturdayInt, sundayInt];
    const [expandedAssetTypes, setExpandedAssetTypes] = useState({});
    const [isInitialising, setIsInitialising] = useState(true);
    const [initialStartDate, setInitialStartDate] = useState(null);

    const [isSearching, setIsSearching] = useState(true);
    const [locationsLoading, setLocationsLoading] = useState(true);
    const [useLocationDefaults, setUseLocationDefaults] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, debounceTimeout);

    const [showFilters, setShowFilters] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState([]);
    const [pageFilters, setPageFilters] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [useHierarchy, setUseHierarchy] = useState(true);
    const inputSearchRef = useRef(null);
    const [startDate, setStartDate] = useState(null);
    //The Monday of the most recent week shown. The window extends backwards from it.
    const [anchorWeek, setAnchorWeek] = useState(null);
    const [recalcPeriodWithDate, setRecalcPeriodWithDate] = useState(null);
    const [searchResultData, setSearchResultData] = useState([]);

    const [lastSearchPayload, setLastSearchPayload] = useState(null);
    const [loadedAssetIds, setLoadedAssetIds] = useState([]);

    const refOverflowTable = useRef();
    const refHeaderTable = useRef();
    const refHeaderScrollListener = useRef();
    const refCellOverflow = useRef([]);
    const refHeaderOverflow = useRef([]);

    const [assetStatusesMappings, setAssetStatusesMappings] = useState([]);
    const [ownershipAttributeMappings, setOwnershipAttributeMappings] = useState([]);
    const tachoAssetTypeIds = useRef([]);
    const tachoAssetTypeSubTypeIds = useRef([]);
    const [vehicleSearchLoaded, setVehicleSearchLoaded] = useState(false);
    const vehicleSearch = useSelector(state => state.filters[filterSearchSelector]);

    const currentSearchTerm = useRef();
    const currentPageNo = useRef();
    const currentStartDate = useRef();
    const currentEndDate = useRef();
    const currentTableSortBy = useRef();
    const currentTableSortDir = useRef();
    const currentAppliedFilters = useRef();
    const currentLocations = useRef();

    // Listings objects:
    const [searchResult, setSearchResult] = useState([]);
    const [activeHeaders, setActiveHeaders] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [sortedBy, setSortedBy] = useState(firstColumnLabel);
    const [sortedDir, setSortedDir] = useState("A");
    const [sortableHeaders, setSortableHeaders] = useState([firstColumnLabel]);
    const [currentPageNumber, setCurrentPageNumber] = useState(1);
    const perPage = defaultItemsPerPage;
    const [columnClasses, setColumnClasses] = useState([]);
    const [columnWidths, setColumnWidths] = useState([cellWidthIdentifier, cellWidthDefault]);

    // Key Modal
    const [showKeyModal, setShowKeyModal] = useState(false);
    const [vehicleActivitiesForKeyModal, setVehicleActivitiesForKeyModal] = useState([]);

    // Report Downloading
    const [reportDownloading, setReportDownloading] = useState(false);

    // DisplayPreferencesModal
    const [displayPreferences, setDisplayPreferences] = useState([]);
    const [showPreferencesModal, setShowPreferencesModal] = useState(false);
    const [tachoWeeksToShow, setTachoWeeksToShow] = useState(4);
    const [preferencesUpdated, setPreferencesUpdated] = useState(false);

    // Day Detail Modal
    const [showDayDetailModal, setShowDayDetailModal] = useState(false);
    const [dayDetailModalAssetId, setDayDetailModalAssetId] = useState(0);
    const [dayDetailModalDate, setDayDetailModalDate] = useState("");

    const reduxDate = useSelector(state => state.filters[vehicleDateReduxName]);
    const [locationSelectorOpen, setLocationSelectorOpen] = useState(false);

    //The window stays anchored on the most recent week, so a new week count moves its start. The
    //selector holds the window start itself, so it has to be re-anchored through recalcPeriodWithDate
    //rather than left to derive a window from the week count alone.
    const handleSavePreferences = (newPreferences, newTachoWeeksToShow) => {
        setDisplayPreferences(newPreferences);
        setTachoWeeksToShow(newTachoWeeksToShow);
        setPreferencesUpdated(true);

        //Without an anchor the window is not initialised yet, so there is nothing to re-anchor.
        if (!anchorWeek) { return; }

        const newStartDate = moment(anchorWeek).add(-(newTachoWeeksToShow - 1), 'W');

        setStartDate(newStartDate);
        setRecalcPeriodWithDate({ startDate: anchorWeek, periodsToShow: newTachoWeeksToShow });
        dispatch(SetFiltering(vehicleDateReduxName, { startDate: newStartDate.toISOString() }));
    };

    function formatPageFilters(statusesForAssets, assetOwnerships, assetTypes) {
        return [{
            "name": "Status",
            "displayName": "Status",
            "items": statusesForAssets
        },
        {
            "name": "Ownership",
            "displayName": "Ownership",
            "items": assetOwnerships
        },
        {
            "name": "AssetType",
            "displayName": "Type",
            "items": assetTypes
        }]
    }

    function processAssetOptions(response) {
        if (response != null) {
            let assetTypes = response.assetTypes;
            if (hasFleet) {
                assetTypes = assetTypes.filter((x) => !x.visOnly);
            }
            tachoAssetTypeIds.current = assetTypes.filter((x) => x.requireAnalysisRules).map((x) => x.id);
            tachoAssetTypeSubTypeIds.current = assetTypes.filter((x) => x.requireAnalysisRules).flatMap(group => group.subtypes?.map(item => item.id)).filter(i => i !== null);
            setPageFilters(formatPageFilters(response.assetStatuses, response.assetOwnerships, assetTypes.filter((x) => x.requireAnalysisRules)));
            setOwnershipAttributeMappings(response.assetOwnerships.map((x) => {
                return { "name": x.name, "colours": x.colour, "id": x.id, "className": x.style, "icon": x.icon }
            }));
            setAssetStatusesMappings(response.assetStatuses.map((x) => {
                return { "name": x.name, "colours": x.colour, "id": x.id }
            }));
        }
    }

    function processActivityTypes(response) {
        if (response != null) {
            setVehicleActivitiesForKeyModal(response.map((x) => {
                return { "name": x.name, "colours": x.colour, "id": x.id }
            }));
        }
    }

    function processTachoPreferences(response) {
        if (response != null) {
            setDisplayPreferences(response.displayOptions);
            setTachoWeeksToShow(response.preferences.tachoWeeksToShow);
        }
    }


       // Function to initialise startDate and initialStartDate
    function initialiseStartDates(weeksToShow) {
        if (reduxDate && reduxDate.startDate) {
            const start = moment(reduxDate.startDate);
            setStartDate(start);
            setAnchorWeek(moment(start).add(weeksToShow - 1, 'W'));
            setInitialStartDate(start);
        } else {
            const start = moment(initialDate).add(-(weeksToShow - 1), 'W');
            setStartDate(start);
            setAnchorWeek(moment(initialDate));
            setInitialStartDate(start);
            dispatch(SetFiltering(vehicleDateReduxName, { startDate: start.toISOString() }));
        }
    }

    
    useEffect(() => {
        recalculateHeaders();
    }, [displayPreferences]);

    useEffect(() => {
        recalculateHeaders();
    }, [startDate]);

    useEffect(() => {

        const scrollHeader = () => {
            if (refHeaderTable.current != null) {
                refHeaderTable.current.style.transform = `translateX(-${refOverflowTable.current.scrollLeft}px)`;
            }
        }

        const removeExistingListeners = () => {
            refOverflowTable.current?.removeEventListener("scroll", scrollHeader);
        }

        if (refOverflowTable.current && refHeaderScrollListener.current == null) {
            //Make sure we detach any existing listeners so its not applied twice
            removeExistingListeners();
            refOverflowTable.current.addEventListener("scroll", scrollHeader);
        }

    }, [refOverflowTable.current]);

    useEffect(() => {
        if (!canAccessPage) {
            return;
        }

        dispatch({ type: 'SITE_FULL_WIDTH' });

        Promise.all([
            AssetService.getAssetOptions(),
            AssetService.getActivityTypes(),
            AssetService.getTachoPreferences()
        ]).then(([assetOptionsResponse, activityTypesResponse, tachoPreferencesResponse]) => {
            processAssetOptions(assetOptionsResponse);
            processActivityTypes(activityTypesResponse);
            processTachoPreferences(tachoPreferencesResponse);
            //The window is anchored on the saved week count, so it cannot be derived until the preference has loaded.
            initialiseStartDates(tachoPreferencesResponse?.preferences?.tachoWeeksToShow ?? tachoWeeksToShow);
        }).catch(() => {
            NotificationToaster.show(Intent.DANGER, `Failed to load Tacho Overview. Please refresh the page.`);
            //The listing still searches without the preferences, so it needs a window to search over.
            initialiseStartDates(tachoWeeksToShow);
        }).finally(() => {
            setIsInitialising(false);
        });
    }, []);

    //A preference save always needs a fresh search: the column values are built from the saved
    //preferences, so an unchanged search payload can still return different columns.
    useEffect(() => {
        if (!startDate || !preferencesUpdated) { return; }

        setPreferencesUpdated(false);
        performSearch(true);
    }, [startDate, preferencesUpdated]);


    //FILTERS:
    const defaultFilters = [{ name: 'Status', displayName: 'Active', value: 1 }, { name: 'Status', displayName: 'Vehicle Off Road', value: 3 }]
    let selectedFilters = useSelector(state => state.filters[filterName]);
    const dispatch = useDispatch();

    useEffect(() => {
        if (!selectedFilters || selectedFilters.length === 0) {
            dispatch(SetFiltering(filterName, defaultFilters));
        }
    }, [selectedFilters])


    function onSelectedLocationsChange(locations) {
        setSelectedLocations(locations);
        setVehicleSearchDispatch(locations, null, null, null, null);
    }

    function onToggleFilter() {
        setShowFilters((prevState) => {
            return !prevState;
        })
    }

    function onFilterChange(listingFilters) {
        setAppliedFilters(listingFilters);
    }

    function onLocationListOpened() {
        setLocationSelectorOpen(true);
    }

    function onLocationListClose() {
        setLocationSelectorOpen(false);
    }

    //Headers Logic/Formatting:
    function recalculateHeaders() {
        if (!startDate) { return; }

        //When start date changes, update the calendar columns:
        let colWidths = [cellWidthIdentifier, cellWidthDefault];
        let calendarHeaders = [];
        for (let i = 0; i < (daysInWeek * tachoWeeksToShow); i++) {
            let thisDay = moment(startDate).add(i, 'days');
            let thisClassNames = "";
            if (weekendDays.includes(thisDay.isoWeekday())) {
                thisClassNames += "weekend";
            }
            calendarHeaders.push(<div key={`cal${thisDay.format("DD")}`} className={classNames(thisClassNames)}>{thisDay.format("ddd")}<br />{thisDay.format("DD")}</div>);
            colWidths.push(cellWidthDay);
        };

        const cellHasOverflow = (index) => {

            var cell = refHeaderOverflow.current[index];

            if (cell != null) {
                return cell.scrollWidth > cell.offsetWidth;
            }

            return false;
        }

        let formattedAdditionalHeaders = displayPreferences.filter(x => x.userSelected).sort((x, y) => x.userPriority - y.userPriority).map((x, headerIndex) => {
            colWidths.push(x.minWidth ?? cellWidthDefault);

            return <Tooltip key={x.name} content={x.name ?? ""} position="top" disabled={!cellHasOverflow(headerIndex)}>
                <div
                    ref={(el) => {
                        refHeaderOverflow.current[headerIndex] = el;
                    }}

                    className="additional-column"
                    style={{ width: x.minWidth }}>
                    {x.name}
                </div>
            </Tooltip>;
        });

        let additionalHeaderKeys = formattedAdditionalHeaders.map((x) => { return x.key.toString() });

        //map the additional columns
        setActiveHeaders([...tableHeadersPrefix, ...calendarHeaders, ...formattedAdditionalHeaders]);
        setSortableHeaders([...tableHeadersPrefix, ...additionalHeaderKeys]);
        setColumnClasses(["base-column", "asset-actions-column base-column", ...Array(calendarHeaders.length).fill("calendar-day"), ...Array(formattedAdditionalHeaders.length).fill("")]);
        setColumnWidths(colWidths);
    }

    useEffect(() => {
        if (!startDate || isInitialising || locationsLoading) { return; }

        recalculateHeaders();
    }, [startDate, isInitialising, locationsLoading]);


    function getIdField(assetId, registrationNumber) {
        return (
            <Tooltip position={Position.TOP} content={`Asset Profile`}>
                <InternalLink link={LinkService.getVehicleProfileUrl(assetId, 'details', "area=TACHO")}>{registrationNumber}</InternalLink>
            </Tooltip>
        );
    }

    //Columns Logic/Formatting:
    function getVehicleIcon(assetTypeId, assetTypeName, assetTypeSubtype) {
        let img = "unknown-vehicle-scaled.svg";
        let vehicleType = assetTypeName || "unknown";
        let subtypeName = assetTypeSubtype?.name;
        var assetType = assetTypesMappings.find(o => {
            return o.id === assetTypeId
        });

        if (assetType) {
            img = assetType.img;
            vehicleType = assetType.name;
        }

        const tooltipText = subtypeName
            ? `${vehicleType} - ${subtypeName}`
            : vehicleType;

        return (
            <div className="vehicle-icon">
                <Tooltip position={Position.TOP} content={tooltipText}>
                    <img src={`/icons/vehicles/${img}`} alt={vehicleType} />
                </Tooltip>
            </div>
        );
    }

    function getCalendarLink(assetId, date) {
        return (
            <Tooltip position={Position.TOP} content={`Vehicle Calendar`}>
                <LinkButton
                    href={`/vehicle/manage/${assetId}/calendar`}
                    icon={"calendar"}
                    externalLink={false}
                    minimal={true}
                    iconOnly={true}
                    text="Calendar"
                    large={false}
                />
            </Tooltip>
        );
    }

    function getCalendarBlockPopoverMessage(activityDescriptions) {
        return (
            <div>
                <p>The data for today is:</p>
                <ul>
                    {activityDescriptions.map((x) => {
                        return <li key={x}>{x}</li>
                    })}
                </ul>
            </div>
        );
    }

    function displayDayDetailModal(assetId, date) {
        setDayDetailModalAssetId(assetId);
        setDayDetailModalDate(date);
        setShowDayDetailModal(true);
    }

    function hideDayDetailModal() {
        setDayDetailModalAssetId(0);
        setDayDetailModalDate("");
        setShowDayDetailModal(false);
    }

    function getCalendarBlock(activityTypes, isAwaitingData, assetId, date) {
        let toolTipDisabled = false;
        if (!activityTypes || activityTypes.length === 0) {
            activityTypes = [{ name: "", colours: '#FFFFFF' }];
            toolTipDisabled = true;
        }
        let formattedDate = date.format("YYYY-MM-DD")
        return (<Tooltip content={getCalendarBlockPopoverMessage(activityTypes.map(x => x.name))} disabled={toolTipDisabled} >
            <KeyBlock colours={activityTypes.map(x => x.colours)} onClick={() => displayDayDetailModal(assetId, formattedDate)} size={14} loading={isAwaitingData} selectable />
        </Tooltip>
        );
    }

    function getCalendarRowsItems(calendarItems, assetId) {
        let isAwaitingData = calendarItems === null;
        if (!calendarItems) {
            calendarItems = Array(daysInWeek * tachoWeeksToShow).fill([]);
        }

        let rows = [];
        for (let i = 0; i < daysInWeek * tachoWeeksToShow; i++) {
            let activities = [];
            if (calendarItems[i] !== undefined) {
                activities = calendarItems[i].map(x => {
                    return vehicleActivitiesForKeyModal.find(y => y.id === x);
                });
            }
            rows.push(getCalendarBlock(activities, isAwaitingData, assetId, moment(startDate).add(i, 'd')));
        }
        return rows;
    }

    function formatColumnDataFromSearchResult(apiData) {
        return apiData.map((x) => {
            return {
                key: x.assetId,
                status: x.assetStatus,
                ownership: x.assetOwnership,
                assetType: x.assetType,
                registrationNumber: x.registrationNumber,
                additionalColumns: x.columns,
                calendarItems: null
            }
        });
    }

    function mapColumnRows(row, rowIndex) {

        let baseColumns = [
            <div className="nowrap inline-items no-spacing" key={`statusIcon_${row.key}`}>
                <AssetStatus statusId={row.status.id} ownershipId={row.ownership.id} isLoading={isInitialising} statusAttributeMappings={assetStatusesMappings} ownershipAttributeMappings={ownershipAttributeMappings} tooltipPosition={Position.RIGHT} />
                {getIdField(row.key, row.registrationNumber)}
            </div>,
            <div className="nowrap inline-items no-spacing" key={`Links_${row.key}`}>
                {getVehicleIcon(
                    row.assetType.id,
                    row.assetType.name,
                    row.assetType.assetTypeSubtype
                )}
                {getCalendarLink(row.key, startDate)}
            </div>, ...getCalendarRowsItems(row.calendarItems, row.key)
        ];

        let noOfBaseColumns = baseColumns.length;

        const cellHasOverflow = (cellRowIndex, cellColIndex) => {
            var rowRefs = refCellOverflow.current[cellRowIndex];

            if (rowRefs != null) {
                var cell = rowRefs[cellColIndex];
                if (cell != null) {
                    return cell.scrollWidth > cell.offsetWidth;
                }
            }

            return false;
        }

        let additionalRows = row.additionalColumns.map((x, colIndex) => {
            if (!displayPreferences) {
                return x.value;
            }
            let preferenceItem = displayPreferences.find(y => y.name === x.name);
            let format = "none";
            if (preferenceItem) {
                format = preferenceItem.format;
            }

            let value = null;

            switch (format) {
                case "DateTime":
                    value = <DateToLocal format="DD/MM/YYYY" invalidDateString="">{x.value}</DateToLocal>;
                    break;
                case "Int":
                    value = <Numeric>{x.value}</Numeric>;
                    break;
                default:
                    value = x.value;
                    break;
            }

            const node = <div
                ref={(el) => {
                    refCellOverflow.current[rowIndex] = refCellOverflow.current[rowIndex] || [];
                    refCellOverflow.current[rowIndex][colIndex] = el;
                }}
                className="additional-column"
                style={{ width: columnWidths[noOfBaseColumns + colIndex] }}
            >
                {value}
            </div>;

            return <Tooltip key={`col-${x.name}`} content={value} position="top" disabled={!cellHasOverflow(rowIndex, colIndex)}>{node}</Tooltip>;
        });
        return baseColumns.concat(additionalRows);
    }

    //Search logic:
    function getSearchPayload() {
        let filters = appliedFilters.map(x => {
            if (x.name === "AssetTypeSubtype" && typeof x.value === "string" && x.value.startsWith("uncat-")) {
                return { key: "AssetType", value: x.parentAssetTypeId };
            }
            return { key: x.name, value: x.value }
        });

        // If no asset type filter is selected, default to tacho-relevant asset types only
        const hasAssetTypeFilter = filters.some(f => f.key === "AssetType" || f.key === "AssetTypeSubtype");
        if (!hasAssetTypeFilter) {
            tachoAssetTypeIds.current.forEach(id => {
                filters.push({ key: "AssetType", value: id });
            });
            tachoAssetTypeSubTypeIds.current.forEach(id => {
                filters.push({ key: "AssetTypeSubtype", value: id });
            });
            
        }

        if (selectedLocations != null) {
            selectedLocations.forEach(function (l) {
                filters = [...filters,
                { key: "Location", value: l }
                ];
            });
        }

        return {
            requestCount: perPage,
            pageNumber: currentPageNumber,
            searchTerm: searchTerm,
            sortField: sortedBy === firstColumnLabel ? "AssetIdentifier" : sortedBy,
            sortDirection: sortedDir,
            filters: filters,
            startDate: startDate.format("YYYY-MM-DD"),
            endDate: moment(startDate).add((daysInWeek * tachoWeeksToShow) - 1, 'days').format("YYYY-MM-DD")
        };
    }

    useEffect(() => {
        if (searchResultData !== undefined && assetStatusesMappings.length > 0 && ownershipAttributeMappings.length > 0) {
            const data = searchResultData.map((d, rowIndex) => {
                return [...mapColumnRows(d, rowIndex)];
            });
            setSearchResult(data);
        }
    }, [searchResultData, assetStatusesMappings, ownershipAttributeMappings]);


    function getSelectedLocationsKey() {
        return [...selectedLocations].sort((a, b) => a - b).join(",");
    }

    function performSearch(forceSearch) {
        const searchPayload = getSearchPayload();

        //This screen always needs to have at least 1 filter, as the default is to have 2 status filters applied
        if(appliedFilters.length === 0) {
            return;
        }

        if (!forceSearch && (!searchPayload ||
            (currentPageNo.current === searchPayload.pageNumber &&
                currentSearchTerm.current === searchPayload.searchTerm &&
                currentStartDate.current === searchPayload.startDate &&
                currentEndDate.current === searchPayload.endDate &&
                currentTableSortBy.current === searchPayload.sortField &&
                currentTableSortDir.current === searchPayload.sortDirection &&
                currentAppliedFilters.current === appliedFilters.length &&
                currentLocations.current === getSelectedLocationsKey()))
        ) {
            //If none of the values have changed, then 2 have been updated at once or it's the initial load from Redux, so prevent the listing from performing another call.
            return;
        }
       
        setIsSearching(true);

        if (currentPageNo.current === searchPayload.pageNumber && currentStartDate.current === searchPayload.startDate && currentEndDate.current === searchPayload.endDate) {
            //The reason for the reload isn't the page number or date changing, so we can reset the page number to 1 but we don't want to reload the page when it happens!
            searchPayload.pageNumber = 1;
            currentPageNo.current = 1;
            setCurrentPageNumber(1);
        } else {
            currentPageNo.current = searchPayload.pageNumber;
        }

        currentSearchTerm.current = searchPayload.searchTerm;
        currentStartDate.current = searchPayload.startDate;
        currentEndDate.current = searchPayload.endDate;
        currentTableSortBy.current = searchPayload.sortField;
        currentTableSortDir.current = searchPayload.sortDirection;
        currentAppliedFilters.current = appliedFilters.length;
        currentLocations.current = getSelectedLocationsKey();

        AssetService.searchAssetsWithPreferences(searchPayload).then((response) => {

            if (response.totalCount === 0) {
                setSearchResultData([]);
                setSearchResult([]);
                setTotalRecords(response.totalCount);
                return;
            }

            if (response.data != null) {
                setSearchResultData(formatColumnDataFromSearchResult(response.data));
            }
            setTotalRecords(response.totalCount);
            setLastSearchPayload(searchPayload);
            setLoadedAssetIds(response.data.map(x => x.assetId));
        }).catch((error) => {
            NotificationToaster.show(Intent.DANGER, `Asset search failed. Please try again. ${error} `);
        }).finally(function () {
            setIsSearching(false);
        });
    }

    function AddTachoDataToSearchResultData(prefSearchResultData, tachoResultData) {
        prefSearchResultData.forEach((x) => {
            let calendarItems = tachoResultData.find(y => y.assetId === x.key);
            x.calendarItems = calendarItems ? calendarItems.activity : null;
        });
        return prefSearchResultData;
    }

    function refreshCalendarData(response) {
        setSearchResultData((srd) => {
            let newSrd = [...srd];
            return AddTachoDataToSearchResultData(newSrd, response);
        });
    }

    function doCalendarRefresh() {
        if (loadedAssetIds.length === 0) { return; }

        refreshCalendarData([]);
        AssetService.searchTachoDataWithAssetIds(loadedAssetIds, startDate.format("YYYY-MM-DD"), tachoWeeksToShow).then((response) => {
            refreshCalendarData(response);
        }).catch((error) => {
            NotificationToaster.show(Intent.DANGER, `Failed to read Tacho data. Calendar may not be populated correctly. Please refresh page and try again. ${error} `);
        }).finally(function () {
            setIsSearching(false);
        });
    }

    useEffect(() => {
        if (selectedLocations.length > 0) {
            setLocationsLoading(false);
        }
    }, [selectedLocations]);


    useEffect(() => {
        doCalendarRefresh();
    }, [loadedAssetIds]);

    useEffect(() => {
        //A save sets the window and the preferences together, and its own forced search covers this commit.
        if (!startDate || preferencesUpdated) { return; }

        //The load's search is owned by the location-aware effect below; searching here would go out
        //before the locations resolve and race it.
        if (isInitialising || locationsLoading || !vehicleSearchLoaded) { return; }

        if (displayPreferences.filter(x => x.userSelected && calculatedColumnNames.includes(x.name)).length > 0) {
            //If the user has any calculated columns displayed, we need to update them as well as the calendar items:
            performSearch();
        }
        else {
            doCalendarRefresh();
        }
    }, [startDate, tachoWeeksToShow, isInitialising, locationsLoading, vehicleSearchLoaded]);

    useEffect(() => {
        if (isInitialising || locationsLoading || !vehicleSearchLoaded || locationSelectorOpen) { return; }
        performSearch();
    }, [debouncedSearchTerm, appliedFilters, selectedLocations, sortedBy, sortedDir, perPage, currentPageNumber, locationsLoading, locationSelectorOpen, isInitialising])

    useEffect(function () {
        if (vehicleSearch && !vehicleSearchLoaded) {
            setSelectedLocations(vehicleSearch.locations);
            setSearchTerm(vehicleSearch.searchTerm);
            setSortedBy(vehicleSearch.sortedBy);
            setCurrentPageNumber(vehicleSearch.currentPageNumber);
            setSortedDir(vehicleSearch.sortedDir);
            setUseLocationDefaults(false);
        } else {
            setUseLocationDefaults(true);
        }
        if (!vehicleSearchLoaded) {
            setVehicleSearchLoaded(true);
        }
    }, [vehicleSearch]);

    function setVehicleSearchDispatch(newLocations, newSearchTerm, newSortedBy, newCurrentPageNumber, newSortedDir) {
        dispatch(SetFiltering(filterSearchSelector, {
            locations: newLocations ?? selectedLocations,
            searchTerm: newSearchTerm ?? searchTerm,
            sortedBy: newSortedBy ?? sortedBy,
            currentPageNumber: newCurrentPageNumber ?? currentPageNumber,
            sortedDir: newSortedDir ?? sortedDir
        }));
    }

    function onSearchTextChange(item) {
        setSearchTerm(item);
        setVehicleSearchDispatch(null, item, null, null, null);
    }

    function openKeyModal() {
        setShowKeyModal(true);
    }

    function closeKeyModal() {
        setShowKeyModal(false);
    }

    function onWeekChange(periodStart) {
        //The selector is mounted skeletoned before the saved window is known, and re-anchoring it
        //then emits a window derived from the default week count.
        if (!initialStartDate) { return; }

        // FDP with reverse=true emits a period-start that is already (selectedDate - N+1 weeks);
        // we just align to Monday for the API.
        let dt = moment(periodStart, periodSelectorDateFormat).startOf('isoWeek');
        if (!dt.isSame(startDate)) {
            setIsSearching(true);
            dispatch(SetFiltering(vehicleDateReduxName, { startDate: dt.toISOString() }));
            setStartDate(dt);
            setAnchorWeek(moment(dt).add(tachoWeeksToShow - 1, 'W'));
            recalculateHeaders();
        }
    }

    function onPagingChange(page) {
        setCurrentPageNumber(page);
        setVehicleSearchDispatch(null, null, null, page, null);
    }

    function onTableSort(header, direction) {
        let sortBy = (typeof header === 'string') ? header : header.props.content;
        setSortedBy(sortBy);
        setSortedDir(direction);
        setVehicleSearchDispatch(null, null, sortBy, null, direction);
    }
    function openPreferencesModal() {
        setShowPreferencesModal(true);
    }

    function closePreferencesModal() {
        setShowPreferencesModal(false);
    }

    function onReportDownloading(isDownloading) {
        setReportDownloading(isDownloading);
    }

    useEffect(() => {
        setTotalPages(Math.ceil(totalRecords / perPage));
    }, [totalRecords, perPage])

    return (
        <UserAccess perform={requiredActions}
            yes={() => (
                <>
                    <div className="contained-content">
                        <div className="contained-content-static">

                            <h1>Tacho Overview</h1>
                            
                            <div className="filter-controls">                            
                                <div className="inline-items">
                                    <div className="inline-item fill">
                                        <div className="form-field-full">
                                            <FormTextInput inputRef={inputSearchRef} placeholder="Search Vehicle" onChange={(x) => onSearchTextChange(x.target.value)} value={searchTerm} large disabled={isInitialising} icon="search" id="user-search-field" />
                                        </div>
                                    </div>
                                    <div className="inline-item">
                                        <FormLocationSelector
                                            businessArea={"Tacho"}
                                            loading={isInitialising}
                                            onLocationListOpened={onLocationListOpened}
                                            onLocationListClose={onLocationListClose}
                                            selectedLocations={selectedLocations}
                                            setSelectedLocations={onSelectedLocationsChange}
                                            useLocationDefaults={useLocationDefaults}
                                            useHierarchy={useHierarchy}
                                            setUseHierarchy={setUseHierarchy}
                                            locationDefaultsToTrue={isInitialising ? null : true}
                                        />
                                    </div>
                                    <div className="inline-item">
                                        <FilterButton onToggle={onToggleFilter} filters={appliedFilters} />
                                    </div>                                
                                </div>
                                <Filter
                                        filterName={filterName}
                                        visible={showFilters}
                                        filters={pageFilters}
                                        onUpdate={onFilterChange}
                                        onClickOutside={onToggleFilter}
                                        numOfColumns={7}
                                        customRenderers={{
                                            AssetType: (props) => (
                                                <AssetTypeFilterRenderer
                                                    {...props}
                                                    expandedAssetTypes={expandedAssetTypes}
                                                    setExpandedAssetTypes={setExpandedAssetTypes}
                                                />
                                            )
                                        }}
                                    />
                            </div>
                            
                            <div className="inline-items spacer-top-small spacer-bottom">
                                <div className="inline-item fill">
                                    <div className="inline-items">
                                        <FormDatePeriodSelector
                                            id="listing-period"
                                            periodType="weeks"
                                            periodsToShow={tachoWeeksToShow}
                                            startDate={(initialStartDate ?? initialDate).format(periodSelectorDateFormat)}
                                            recalcPeriodWithDate={recalcPeriodWithDate}
                                            resetDate={initialDate}
                                            onChange={onWeekChange}
                                            dateFormat={periodSelectorDateFormat}
                                            showCalendar={true}
                                            showText={false}
                                            reverse={true}
                                            loading={!initialStartDate}
                                        />
                                        <div className="inline-divide"></div>
                                        <div className="button-row">
                                            <Tooltip content="Add new asset">
                                                <LinkButton intent="primary" large={false} icon="plus" iconOnly href="/asset/create/business-area/1" id="asset-create" />
                                            </Tooltip>
                                            <Tooltip content="Reports">
                                                <ReportList compact lastSearchPayload={lastSearchPayload} onReportDownloadingChange={onReportDownloading} pageName='Fleet' large={false} />
                                            </Tooltip>
                                            <Tooltip content="Key">
                                                <Button intent="secondary" icon="properties" iconOnly id="key" onClick={openKeyModal} className="key-button" large={false} />
                                            </Tooltip>
                                        </div>
                                        <div className="inline-divide"></div>
                                        <div className="button-row">
                                            <Button text="Column Preferences" minimal={true} large={false} intent="secondary" icon="cog" id="configure-properties" className="configure-prefs-button" onClick={openPreferencesModal}
                                                loading={isInitialising}
                                                disabled={isInitialising || isSearching} />
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <ListingPagination currentPage={currentPageNumber} totalPages={totalPages} onPageChange={onPagingChange} loadingData={isSearching || isInitialising} className="pagination-inline" />
                                </div>
                            </div>


                            <NotificationInline intent="success" text="Your report has been queued, and will download as soon as it's ready." show={reportDownloading} />

                            <div ref={refHeaderTable}>
                                <ListingTable
                                    id="listing-table-vehicle-headers"
                                    headers={activeHeaders}
                                    data={[]}
                                    totalRecordCount={1}
                                    loadingData={false}
                                    sortable
                                    sortableHeaders={sortableHeaders}
                                    sortedBy={sortedBy}
                                    sortedDir={sortedDir}
                                    onSort={onTableSort}
                                    columnClasses={columnClasses}
                                    columnWidths={columnWidths}
                                    loadingClass={searchResultData.length === 0 ? "bp6-skeleton" : "bp6-skeleton loading"}
                                    pageable={false}
                                    fitToContent
                                />
                            </div>

                        </div>

                        <div className="contained-content-auto" ref={refOverflowTable}>
                            <ListingTable
                                id="listing-table-fleet"
                                headers={[]}
                                data={searchResult}
                                totalRecordCount={totalRecords}
                                onPagingChange={onPagingChange}
                                loadingData={isSearching || isInitialising}
                                noDataHeading="No Assets Found"
                                noDataMessage="No assets for this search criteria"
                                sortable
                                sortableHeaders={sortableHeaders}
                                sortedBy={sortedBy}
                                sortedDir={sortedDir}
                                onSort={onTableSort}
                                columnClasses={columnClasses}
                                columnWidths={columnWidths}
                                loadingClass={searchResultData.length === 0 ? "bp6-skeleton" : "bp6-skeleton loading"}
                                pageable={false}
                                fitToContent
                            />
                        </div>
                    </div>

                    <KeyModal
                        showKeyModal={showKeyModal}
                        closeKeyModal={closeKeyModal}
                        isInitialising={isInitialising}
                        ownershipForKeyModal={ownershipAttributeMappings}
                        assetStatusesForKeyModal={assetStatusesMappings}
                        vehicleActivitiesForKeyModal={vehicleActivitiesForKeyModal}
                    />

                    <DayDetailModal
                        showDayDetailModal={showDayDetailModal}
                        closeDayDetailModal={hideDayDetailModal}
                        assetId={dayDetailModalAssetId}
                        date={dayDetailModalDate}
                        statusAttributeMappings={assetStatusesMappings}
                        ownershipAttributeMappings={ownershipAttributeMappings}
                        lastSearchPayload={lastSearchPayload}
                        vehicleActivityTypes={vehicleActivitiesForKeyModal}
                        key={dayDetailModalAssetId + dayDetailModalDate}
                    />

                    <PreferencesModal
                        isOpen={showPreferencesModal}
                        onClose={closePreferencesModal}
                        displayPreferences={displayPreferences}
                        numberOfWeeks={tachoWeeksToShow}
                        cancelButtonIntent={Intent.NONE}
                        onSave={handleSavePreferences}
                        onSavePreferences={AssetService.saveTachoPreferences}
                        preferencesKey="Tacho"
                    />
                </>
            )}
            no={() => <PageRestricted />}
        />
    );
}